import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormGroup,
  Paper,
  Divider,
  Alert,
  FormControl,
  FormHelperText,
  Tooltip,
  IconButton
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import Signature from 'src/ui-component/Signature';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { handleInsertSubcodeDocs } from './TechnicalSections/uploadSubcodeDocs';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { updateFormData } from 'src/redux-store/slices/constructionFormSlice';
import { RootState } from 'src/redux-store/appStore';
import LoadingOverlay from 'src/components/common/LoadingOverlay';

export interface OwnerCertifications
{
     newHomeConstruction : boolean,
     personallyPreparedPlans : boolean,
     willSuperviseWork : boolean,
     agreesToAdviseContractors : boolean
}
export interface WorkTypes
{
    building: boolean,
    fireProtection: boolean,
    plumbing: boolean,
    electrical: boolean

}

interface CertificationTermsProps {
  role: string;
  onAccept: (accepted: boolean) => void;
  DefaultUrl?: string;
}

// Usage example component
// interface LieuOfOathProps {
//   userRole: string;
// }

export const CertificationTerms: React.FC<CertificationTermsProps> = ({
  role,
  onAccept,
  DefaultUrl = ''
}) => {

  const dispatch = useDispatch()
  const formData = useSelector(
    (state: RootState) => state.constructionFormSlice.formData
  );
  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [accepted, setAccepted] = useState(formData?.LieuAuthCondition?.IsAccept === "false" ? false : true || false);
  onAccept(formData?.LieuAuthCondition?.IsAccept || false)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [signature, setSignature] = useState<{
    imageURL: string;
    file: { name: string };
  } | null>(null);
  const currentDate = new Date().toISOString().split('T')[0]; // Format as YYYY-MM-DD
  const [date, setDate] = useState(currentDate);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Add these state variables to your component
  const [documentUrl, setDocumentUrl] = useState<string | null>(formData?.LieuAuthCondition?.LieuAuthPdfUrl || null);
  const [fileName, setFileName] = useState<string | null>(formData?.LieuAuthCondition?.FileName || null);

  // Form data for non-owner roles
  const [agentData, setAgentData] = useState({
    isContractor: false,
    name: '',
    telephone: '',
    address: '',
    leadHazardAbatement: false,
    homeElevator: false
  });

  // Owner certification checkboxes state
  const [ownerCertifications, setOwnerCertifications] = useState<OwnerCertifications>({
     newHomeConstruction : false,
     personallyPreparedPlans : false,
     willSuperviseWork : false,
     agreesToAdviseContractors : false
  });

  // Work type selections for owners
  const [workTypes, setWorkTypes] = useState<WorkTypes>({
    building: false,
    fireProtection: false,
    plumbing: false,
    electrical: false
  });

  // Create a ref for the content to be converted to PDF
  const certificationRef = React.useRef<HTMLDivElement>(null);

  // Check if at least one work type is selected when option C is checked
  const hasSelectedWorkType = Object.values(workTypes).some(Boolean);

  // Helper function to handle owner certification checkbox changes
  const handleOwnerCertChange =
    (key: keyof typeof ownerCertifications) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setOwnerCertifications({
          ...ownerCertifications,
          [key]: event.target.checked
        });

        // If the parent checkbox (cert 'c') is unchecked, clear all work type checkboxes
        if (key === 'willSuperviseWork' && !event.target.checked) {
          setWorkTypes({
            building: false,
            fireProtection: false,
            plumbing: false,
            electrical: false
          });
        }

        // Clear related errors when checkbox is checked
        if (event.target.checked && key === 'agreesToAdviseContractors' && formErrors.adviseContractors) {
          setFormErrors((prev) => {
            const updated = { ...prev };
            delete updated.adviseContractors;
            return updated;
          });
        }
      };

  // Helper function to handle work type checkbox changes
  const handleWorkTypeChange =
    (key: keyof typeof workTypes) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setWorkTypes({
          ...workTypes,
          [key]: event.target.checked
        });

        // Clear work type error if at least one is now selected
        if (event.target.checked && formErrors.workTypes) {
          setFormErrors((prev) => {
            const updated = { ...prev };
            delete updated.workTypes;
            return updated;
          });
        }
      };

  // Helper function to handle agent data changes
  const handleAgentDataChange =
    (key: keyof typeof agentData) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setAgentData({
          ...agentData,
          [key]:
            key === 'isContractor' ||
              key === 'leadHazardAbatement' ||
              key === 'homeElevator'
              ? event.target.checked
              : event.target.value
        });

        // Clear any error for this field
        if (formErrors[key]) {
          setFormErrors((prev) => {
            const updated = { ...prev };
            delete updated[key];
            return updated;
          });
        }
      };

  // Open certification dialog
  const handleCheckboxClick = () => {
    setOpenDialog(true);
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  // Validate if at least one certification is selected
  const atLeastOneCertificationSelected =
    Object.values(ownerCertifications).some(Boolean);

  // Function to generate and upload PDF
  const generatePDF = async () => {
    if (!certificationRef.current) return;

    try {
      // Create a clone of the element to modify without affecting the original
      const clone = certificationRef.current.cloneNode(true) as HTMLElement;
      const tempDiv = document.createElement("div");
      tempDiv.appendChild(clone);
      document.body.appendChild(tempDiv);

      // Find and remove buttons and form elements that shouldn't appear in the PDF
      const buttonsToRemove = clone.querySelectorAll('button, .btn, [type="button"], .clear-btn, .save-btn');
      buttonsToRemove.forEach(btn => btn.remove());

      // Convert text input fields to plain text to avoid form styling
      const inputFields = clone.querySelectorAll('input[type="text"], input[type="date"]');
      inputFields.forEach(input => {
        const inputElement = input as HTMLInputElement;
        const span = document.createElement('span');
        span.textContent = inputElement.value;
        span.style.fontFamily = 'Arial, sans-serif';
        span.style.fontSize = '14px';
        span.style.border = 'none';
        if (input.parentNode) {
          input.parentNode.replaceChild(span, input);
        }
      });

      // Style the clone for better PDF rendering
      clone.style.width = '800px';
      clone.style.padding = '40px';
      clone.style.backgroundColor = 'white';
      clone.style.fontFamily = 'Arial, sans-serif';

      // Don't create custom checkboxes, better approach:
      // Find the containers that have duplicated checkboxes
      const checkboxContainers = clone.querySelectorAll('.checkbox-container, .form-check, [role="checkbox"]');
      checkboxContainers.forEach(container => {
        const containerElement = container as HTMLElement;
        const checkboxes = containerElement.querySelectorAll('input[type="checkbox"]');

        // If there's more than one checkbox in the container, keep only the first one
        if (checkboxes.length > 1) {
          // eslint-disable-next-line no-plusplus
          for (let i = 1; i < checkboxes.length; i++) {
            if (checkboxes[i].parentNode) {
              checkboxes[i].parentNode.removeChild(checkboxes[i]);
            }
          }
        }
      });

      // Now style the remaining checkboxes
      const remainingCheckboxes = clone.querySelectorAll('input[type="checkbox"]');
      remainingCheckboxes.forEach(checkbox => {
        const checkboxElement = checkbox as HTMLInputElement;

        // Make the original checkbox more visible/prominent
        checkboxElement.style.width = '16px';
        checkboxElement.style.height = '16px';
        checkboxElement.style.accentColor = '#2196F3';
        checkboxElement.style.border = '2px solid #2196F3';
        checkboxElement.style.borderRadius = '3px';
        checkboxElement.style.outline = 'none';
        checkboxElement.style.marginRight = '8px';
      });

      // Style the signature pad area to look cleaner
      const signaturePadElement = clone.querySelector('.signature-pad, [data-testid="signature-pad"]');
      if (signaturePadElement) {
        const signaturePad = signaturePadElement as HTMLElement;
        signaturePad.style.border = '1px solid #ccc';
        signaturePad.style.borderRadius = '4px';
        signaturePad.style.backgroundColor = '#f9f9f9';
        signaturePad.style.padding = '10px';
      }

      // Add a professional header with title if needed
      const headerDiv = document.createElement('div');
      headerDiv.style.textAlign = 'center';
      headerDiv.style.marginBottom = '20px';
      headerDiv.style.borderBottom = '2px solid #2196F3';
      headerDiv.style.paddingBottom = '10px';

      const headerTitle = document.createElement('h2');
      headerTitle.textContent = 'CERTIFICATION IN LIEU OF OATH';
      headerTitle.style.margin = '5px 0';
      headerTitle.style.color = '#2196F3';
      headerDiv.appendChild(headerTitle);

      // Remove any existing headers that might be causing duplication
      const existingHeaders = clone.querySelectorAll('h1, h2, h3');
      existingHeaders.forEach(header => {
        if (header.textContent?.includes('CERTIFICATION IN LIEU OF OATH')) {
          if (header.parentNode) {
            header.parentNode.removeChild(header);
          }
        }
      });

      // Only add our custom header if there isn't already one
      if (!clone.querySelector('h2, h1')?.textContent?.includes('CERTIFICATION IN LIEU OF OATH')) {
        clone.insertBefore(headerDiv, clone.firstChild);
      }

      // Render to canvas with improved settings
      const canvas = await html2canvas(clone, {
        scale: 2, // Higher resolution for sharper text
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: '#ffffff',
        imageTimeout: 15000, // Longer timeout for complex renders
        removeContainer: true // Clean up the temporary container
      });

      // Clean up the temporary elements
      if (tempDiv.parentNode) {
        tempDiv.parentNode.removeChild(tempDiv);
      }

      // Generate PDF with improved configuration
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true // Better compression
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      // Calculate ratio to fit content perfectly on the page with margins
      const margin = 10; // 10mm margins
      const availableWidth = pdfWidth - (2 * margin);
      const availableHeight = pdfHeight - (2 * margin);
      const ratio = Math.min(availableWidth / imgWidth, availableHeight / imgHeight);

      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = margin;

      pdf.addImage(
        imgData,
        'PNG',
        imgX,
        imgY,
        imgWidth * ratio,
        imgHeight * ratio
      );

      // Add metadata to the PDF
      pdf.setProperties({
        title: 'Property Owner Certification',
        subject: 'New Jersey Property Certification',
        creator: 'Certification System',
        author: 'Property Owner',
        keywords: 'certification, property, new jersey'
      });

      // Create a descriptive filename with the current date
      // const fileName = `NJ_Property_Certification_${role?.toLowerCase() || 'owner'}_${new Date()
      //   .toISOString()
      //   .slice(0, 10)}.pdf`;

      // Get the PDF as a blob for uploading
      const pdfBlob = pdf.output('blob');

      // Create a File object from the blob with proper PDF extension
      const currentDate1 = new Date().toISOString().slice(0, 10);
      const fileNameWithExtension = `NJ_Property_Certification_${role?.toLowerCase() || 'owner'}_${currentDate1}.pdf`;

      // Create a File object with the correct MIME type
      const pdfFile = new File([pdfBlob], fileNameWithExtension, {
        type: 'application/pdf',
        lastModified: new Date().getTime()
      });

      // Upload the PDF file using the provided function
      try {
        const uploadResponse = await handleInsertSubcodeDocs(pdfFile, 'Construction', 'LieuAuth');

        if (uploadResponse) {
          setDocumentUrl(uploadResponse.DownloadURL);
          setFileName(uploadResponse.FileName || 'Certification Document.pdf');
          dispatch(updateFormData({
            LieuAuthCondition: {
              IsAccept: true,
              LieuAuthPdfUrl: uploadResponse.DownloadURL,
              FileName: uploadResponse.FileName
            }
          }));
          // Show success message
          toast.success('Certification document uploaded successfully');
          return uploadResponse;
        }
      } catch (uploadError) {
        console.error('Error uploading PDF:', uploadError);
        // Still save the PDF locally as a fallback
        pdf.save(fileNameWithExtension);
        toast.error('Error uploading document. PDF has been saved locally.');
      }

      return true;
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error generating PDF document');
      return false;
    }
  };

  // Handle certification acceptance
  const handleAcceptCertification = async () => {
    const errors: Record<string, string> = {};
    setIsLoading(true);
    // Validate based on role
    if (role === 'Owner') {
      // Validate that at least one certification option is selected
      if (!atLeastOneCertificationSelected) {
        errors.certifications =
          'Please select at least one certification option';
      }

      // Always validate checkbox D (required for all owner submissions)
      if (!ownerCertifications.agreesToAdviseContractors) {
        errors.adviseContractors =
          'You must agree to advise contractors of tax requirements';
      }

      // If option C is selected, validate that at least one work type is selected
      if (ownerCertifications.willSuperviseWork && !hasSelectedWorkType) {
        errors.workTypes = 'Please select at least one work type';
      }

      // Validate signature
      if (!signature) {
        errors.signature = 'Signature is required';
      }

      // Validate date
      if (!date) {
        errors.date = 'Date is required';
      }
    } else {
      // For agents or contractors
      if (!agentData.name.trim()) {
        errors.name = 'Name is required';
      }
      if (!agentData.telephone.trim()) {
        errors.telephone = 'Telephone is required';
      }
      if (!signature) {
        errors.signature = 'Signature is required';
      }
      if (!date) {
        errors.date = 'Date is required';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // Clear any errors
    setFormErrors({});

    // Generate and download PDF
    const pdfSuccess = await generatePDF();

    // Only accept terms if PDF was successfully generated
    if (pdfSuccess) {
      // Accept terms
      setAccepted(true);
      onAccept(true);
      setOpenDialog(false);
    }
    setIsLoading(false);
  };

  // Effect to update form errors when certification options change
  useEffect(() => {
    // Clear certification error if at least one is now selected
    if (atLeastOneCertificationSelected && formErrors.certifications) {
      setFormErrors((prev) => {
        const updated = { ...prev };
        delete updated.certifications;
        return updated;
      });
    }

    // Clear work type error if option C is unchecked
    if (!ownerCertifications.willSuperviseWork && formErrors.workTypes) {
      setFormErrors((prev) => {
        const updated = { ...prev };
        delete updated.workTypes;
        return updated;
      });
    }
  }, [ownerCertifications, atLeastOneCertificationSelected, formErrors]);

  const getSignature = async (signatureData: {
    imageURL: string;
    file: { name: string };
  }) => {
    setSignature(signatureData);
    // Clear any signature errors
    setFormErrors((prev) => {
      const updated = { ...prev };
      delete updated.signature;
      return updated;
    });
  };

  return (
    <Box>
      {documentUrl && (
        <Box mb={2} sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" color="text.primary" sx={{ mr: 0.5 }}>
            {fileName || 'Certification Document.pdf'}
          </Typography>
          <Tooltip title="Download Certification PDF">
            <IconButton
              component="a"
              href={documentUrl}
              target="_blank"
              rel="noopener"
              color="primary"
              size="small"
              sx={{
                padding: 0.5,
                '&:hover': {
                  backgroundColor: 'action.hover',
                }
              }}
            >
              <CloudDownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      {/* Terms and Conditions Checkbox */}
      {<FormControlLabel
        control={
          <Checkbox
            checked={accepted}
            onClick={handleCheckboxClick}
            id="certification-terms-checkbox"
            disabled={documentUrl !== null}
          />
        }
        label={
          <Typography variant="body1">
            I agree to the Certification in Lieu of Oath terms and conditions
          </Typography>
        }
      />}

      {/* Certification Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        aria-labelledby="certification-dialog-title"
      >
        {isLoading && <LoadingOverlay />}
        <DialogTitle id="certification-dialog-title">
          <Typography variant="h6" fontWeight="bold">
            CERTIFICATION IN LIEU OF OATH
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2 }}>
          {Object.keys(formErrors).length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Please correct the following errors before proceeding:
              <ul>
                {Object.values(formErrors).map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}

          {/* Content to be converted to PDF */}
          <div ref={certificationRef}>
            {/* Owner certification section */}
            {role === 'Owner' && (
              <Box>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    mb: 3,
                    border: formErrors.certifications ? '1px solid red' : 'none'
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    I hereby certify that I am the owner in fee of the property.
                  </Typography>

                  {formErrors.certifications && (
                    <FormHelperText error>
                      {formErrors.certifications}
                    </FormHelperText>
                  )}

                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    sx={{ mt: 2, mb: 1 }}
                  >
                    Mark the following applicable boxes:
                  </Typography>

                  <Grid container spacing={1} sx={{ mt: 1 }}>
                    <Grid item xs={1}>
                      <Checkbox
                        checked={ownerCertifications.newHomeConstruction}
                        onChange={handleOwnerCertChange('newHomeConstruction')}
                        id="owner-cert-a"
                        inputProps={{
                          'aria-label': 'New home construction certification'
                        }}
                      />
                    </Grid>

                    <Grid item xs={11}>
                      <Typography
                        variant="body2"
                        sx={{ color: 'primary.main' }}
                      >
                        I further certify that a new home (private residence)
                        will be constructed on this property for my own use and
                        occupancy. This dwelling is to be occupied by myself and
                        is not to be used for any purpose other than single
                        family residential use. I attest that all construction,
                        plumbing, or electrical work will be done, in whole or
                        in part, by me or by subcontractors under my
                        supervision, in accordance with all applicable laws;
                        and, I further acknowledge that said new home is not
                        covered under the New Home Warranty and Builders
                        Registration Act (N.J.S.A. 46:3B-1 et seq.) and that
                        such fact shall be disclosed to any person purchasing
                        this property within ten years of the date of issuance
                        of a certificate of occupancy.
                      </Typography>
                      {ownerCertifications.newHomeConstruction && (
                        <Box
                          sx={{
                            mt: 1,
                            p: 1,
                            bgcolor: 'info.light',
                            borderRadius: 1
                          }}
                        >
                          <Typography variant="body2" fontWeight="bold">
                            I UNDERSTAND THAT IN MARKING BOX A, I ACKNOWLEDGE
                            THAT I AM ASSUMING RESPONSIBILITY FOR THE WORK DONE
                            ON SAID PROPERTY, THE CONDITION OF THE PROPERTY
                            PRIOR TO, DURING, AND AFTER ANY WORK PERFORMED, AND
                            FOR THE PERFORMANCE OF THE SUBCONTRACTORS I HIRE,
                            EMPLOY, OR OTHERWISE CONTRACT OR WITH WHOM I MAKE
                            AGREEMENTS TO PERFORM WORK. I AM VOLUNTARILY AND
                            KNOWINGLY ASSUMING THIS RESPONSIBILITY.
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  </Grid>

                  <Grid container spacing={1} sx={{ mt: 2 }}>
                    <Grid item xs={1}>
                      <Checkbox
                        checked={ownerCertifications.personallyPreparedPlans}
                        onChange={handleOwnerCertChange('personallyPreparedPlans')}
                        id="owner-cert-b"
                        inputProps={{
                          'aria-label':
                            'Personally prepared plans certification'
                        }}
                      />
                    </Grid>

                    <Grid item xs={11}>
                      <Typography
                        variant="body2"
                        sx={{ color: 'primary.main' }}
                      >
                        I further certify the following as required by the New
                        Jersey Uniform Construction Code, N.J.A.C.
                        5:23-2.15(f)1.ix:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: 1, color: 'primary.main' }}
                      >
                        I personally prepared the plans submitted for: 1) the
                        new home referred to in A; or, 2) an addition,
                        alteration, renovation, or repair to an existing single
                        family residence owned and occupied by myself and
                        located on the property listed on Page 1; or, 3) a new
                        structure that will be physically separate from, but
                        that will be deemed part of, an existing single family
                        residence that is owned and occupied by myself and
                        located on the property listed on Page 1.
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={1} sx={{ mt: 2 }}>
                    <Grid item xs={1}>
                      <Checkbox
                        checked={ownerCertifications.willSuperviseWork}
                        onChange={handleOwnerCertChange('willSuperviseWork')}
                        id="owner-cert-c"
                        inputProps={{
                          'aria-label': 'Will perform work certification'
                        }}
                      />
                    </Grid>

                    <Grid item xs={11}>
                      <Typography
                        variant="body2"
                        sx={{ color: 'primary.main' }}
                      >
                        I further certify that I will perform or supervise the
                        following work:
                      </Typography>

                      <FormControl
                        error={!!formErrors.workTypes && ownerCertifications.willSuperviseWork }
                        component="fieldset"
                        sx={{ width: '100%' }}
                      >
                        <FormGroup
                          sx={{ display: 'flex', flexDirection: 'row', mt: 1 }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={workTypes.building}
                                onChange={handleWorkTypeChange('building')}
                                disabled={!ownerCertifications.willSuperviseWork }
                              />
                            }
                            label="Building"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={workTypes.fireProtection}
                                onChange={handleWorkTypeChange(
                                  'fireProtection'
                                )}
                                disabled={!ownerCertifications.willSuperviseWork }
                              />
                            }
                            label="Fire Protection"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={workTypes.plumbing}
                                onChange={handleWorkTypeChange('plumbing')}
                                disabled={!ownerCertifications.willSuperviseWork}
                              />
                            }
                            label="Plumbing"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={workTypes.electrical}
                                onChange={handleWorkTypeChange('electrical')}
                                disabled={!ownerCertifications.willSuperviseWork}
                              />
                            }
                            label="Electrical"
                          />
                        </FormGroup>
                        {formErrors.workTypes && ownerCertifications.willSuperviseWork && (
                          <FormHelperText error>
                            {formErrors.workTypes}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>

                  <Grid container spacing={1} sx={{ mt: 2 }}>
                    <Grid item xs={1}>
                      <FormControl
                        error={!!formErrors.adviseContractors}
                        component="fieldset"
                      >
                        <Checkbox
                          checked={ownerCertifications.agreesToAdviseContractors}
                          onChange={handleOwnerCertChange('agreesToAdviseContractors')}
                          size="small"
                          id="owner-cert-d"
                          inputProps={{
                            'aria-label': 'Advise contractors certification'
                          }}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={11}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: formErrors.adviseContractors
                            ? 'error.main'
                            : 'primary.main'
                        }}
                      >
                        I agree to advise all contractors on this project that
                        they are required to be registered with the New Jersey
                        Division of Taxation and to comply with all New Jersey
                        tax laws.
                        <span style={{ color: 'error.main' }}>*</span>
                      </Typography>
                      {formErrors.adviseContractors && (
                        <FormHelperText error>
                          {formErrors.adviseContractors}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                </Paper>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" paragraph>
                  I further certify the following as required by the Uniform
                  Construction Code, N.J.A.C. 5:23-2.15(a)5: All required State,
                  county, and local prior approvals, including such
                  certification as the construction official may require, have
                  been given or will be given prior to permit issuance.
                </Typography>

                <Typography variant="body2" paragraph>
                  I understand that if any of the above statements are willfully
                  false, I am subject to punishment.
                </Typography>

                <Box sx={{ my: 3 }}>
                  <Typography variant="body2">
                    Date:<span style={{ color: 'error.main' }}>*</span>
                  </Typography>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <TextField
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        required
                        error={!!formErrors.date}
                        helperText={formErrors.date}
                        InputLabelProps={{ shrink: true }}
                        sx={{ mt: 0.5 }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" gutterBottom>
                    Signature:<span style={{ color: 'error.main' }}>*</span>
                  </Typography>
                  {formErrors.signature && (
                    <Typography color="error" variant="caption">
                      {formErrors.signature}
                    </Typography>
                  )}
                  <Signature
                    getSignature={getSignature}
                    DefaultUrl={DefaultUrl}
                  />
                </Box>
              </Box>
            )}

            {/* Agent/Contractor certification section */}
            {role !== 'Owner' && (
              <Box sx={{ mt: 1 }}>
                <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                  <Typography variant="body1" paragraph>
                    I hereby certify the following as required by the Uniform
                    Construction Code, N.J.A.C. 5:23-2.15(d): the proposed work
                    is authorized by the owner in fee; and I have been
                    authorized by the owner in fee to make this application as
                    his agent.
                  </Typography>

                  <Typography variant="body2" paragraph>
                    I further certify the following as required by the Uniform
                    Construction Code, N.J.A.C. 5:23-2.15(a)5: All required
                    State, county, and local prior approvals, including such
                    certification as the construction official may require, have
                    been given or will be given prior to permit issuance.
                  </Typography>

                  <Typography variant="body2" paragraph>
                    I agree to advise all contractors on this project that they
                    are required to be registered with the New Jersey Division
                    of Taxation and to comply with all New Jersey tax laws.
                  </Typography>

                  <Typography variant="body2" paragraph>
                    I understand that if any of the above statements are
                    willfully false, I am subject to punishment.
                  </Typography>
                </Paper>

                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={agentData.isContractor}
                          onChange={handleAgentDataChange('isContractor')}
                          id="is-contractor-checkbox"
                        />
                      }
                      label="Check if contractor"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      Agent Name:<span style={{ color: 'error.main' }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={agentData.name}
                      onChange={handleAgentDataChange('name')}
                      error={!!formErrors.name}
                      helperText={formErrors.name}
                      required
                      sx={{ mt: 1 }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      Telephone:<span style={{ color: 'error.main' }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={agentData.telephone}
                      onChange={handleAgentDataChange('telephone')}
                      error={!!formErrors.telephone}
                      helperText={formErrors.telephone}
                      required
                      sx={{ mt: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      Date:<span style={{ color: 'error.main' }}>*</span>
                    </Typography>
                    <TextField
                      type="date"
                      fullWidth
                      variant="outlined"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      size="small"
                      required
                      error={!!formErrors.date}
                      helperText={formErrors.date}
                      InputLabelProps={{ shrink: true }}
                      sx={{ mt: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">Address:</Typography>
                    <TextField
                      fullWidth
                      minRows={2}
                      variant="outlined"
                      multiline
                      size="small"
                      value={agentData.address}
                      onChange={handleAgentDataChange('address')}
                      sx={{ mt: 1 }}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" gutterBottom>
                    Signature:<span style={{ color: 'error.main' }}>*</span>
                  </Typography>
                  {formErrors.signature && (
                    <Typography color="error" variant="caption">
                      {formErrors.signature}
                    </Typography>
                  )}
                  <Signature
                    getSignature={getSignature}
                    DefaultUrl={DefaultUrl}
                  />
                </Box>

                <Paper elevation={1} sx={{ p: 2, mt: 3 }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    Additional Certifications:
                  </Typography>

                  <Grid container spacing={1} sx={{ mt: 1 }}>
                    <Grid item xs={1}>
                      <Checkbox
                        checked={agentData.leadHazardAbatement}
                        onChange={handleAgentDataChange('leadHazardAbatement')}
                      />
                    </Grid>

                    <Grid item xs={11}>
                      <Typography
                        variant="body2"
                        sx={{ color: 'primary.main' }}
                      >
                        LEAD HAZARD ABATEMENT: Include Homeowner or Building
                        Owner Affidavit as per N.J.A.C. 5:17
                      </Typography>
                    </Grid>

                    <Grid item xs={1}>
                      <Checkbox
                        checked={agentData.homeElevator}
                        onChange={handleAgentDataChange('homeElevator')}
                      />
                    </Grid>

                    <Grid item xs={11}>
                      <Typography
                        variant="body2"
                        sx={{ color: 'primary.main' }}
                      >
                        HOME ELEVATOR: Include Home Elevation Contractor
                        Certification as per N.J.S.A 52:27D-123.16.
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            )}
          </div>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', p: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Fields marked with <span style={{ color: 'error.main' }}>*</span>{' '}
            are required
          </Typography>
          <Box>
            <Button onClick={handleDialogClose} color="primary" sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button
              onClick={handleAcceptCertification}
              color="primary"
              variant="contained"
            >
              Accept Terms & Download PDF
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
